import type {
  CloudServiceExternalItem,
  ComputersPageViewModel,
  ConnectedEmployeeExternalItem,
  ConnectedPartnerExternalItem,
  InfosecSettings,
  RemoteServerExternalItem,
} from 'src/pages/computers-page/computers-page-view-model.js';
import {
  type ComputerViewModelExtended,
  type PersonalDataItemViewModelExtended,
  type SuggestedRiskAssessment,
  suggestedRiskAssessmentsForDataItems,
} from 'src/store/selectors';
import {
  antiVirusRequirementsOptions,
  assetsNotDeleted,
  computersForMap,
  computersNotDeleted,
  contractsNotDeletedWithParties,
  employeesNotDeleted,
  externalConnectionsForMap,
  getOrganization,
  getOrganizationId,
  mobileMedicalDataRequirementsOptions,
  networksForMap,
  networksNotDeleted,
  offlinePersonalDataItems,
  partnerNamesById,
  partnersNotDeleted,
  personalDataItemsNotDeleted,
  riskAssessmentsNotDeleted,
  standaloneComputers,
  standalonePrinters,
  variantSimplifiedPrivacy,
} from 'src/store/selectors';
import type {
  ExternalConnectionViewModel,
  NetworkViewModel,
  PersonalDataItemViewModel,
  RiskAssessmentViewModel,
} from '@trinnvis/dabih-api-client';
import { EmployeeViewModelStatusEnum, NetworkViewModelNetworkTypeEnum } from '@trinnvis/dabih-api-client';
import type { State } from 'src/store/types.js';
import _, { sortBy } from 'lodash';
import { displayName, entityNameForLists } from 'src/utilities/text';
import type { PageLinks } from 'src/pages/overview-page/overview-page-view-model';
import type { AbstractPageView } from 'src/pages/abstract-page-view';
import type { InfosecDataItem } from '../../pages/computers-page/infosec-procedure/infosecDataItem';
import type { InfosecStorageUnit } from '../../pages/computers-page/infosec-procedure/infosecStorageUnit';
import type { InfosecExternalConnection } from '../../pages/computers-page/infosec-procedure/infosecExternalConnection';
import type { Network } from '../../pages/computers-page/network';
import type { ComputerMapLocal } from '../../pages/computers-page/computer-map-local';
import type { ComputerMapPrinter } from '../../pages/computers-page/computer-map-printer';
import type { RelatedRiskAssessment } from '../../library/lists/related-risk-assessment';
import type { DataItem } from '../../pages/computers-page/data-item';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';
import { toAttachmentItems } from '../to-attachment-items';
import type { ApiClient } from '../../store/backend';
import { getPages } from '../../store/selectors/pages/getPages';
import { getPage } from '../../store/selectors/pages/getPage';
import { mappedDataItems } from './mapped-data-items';
import { mappedRemoteServers } from './mapped-remote-servers';
import { mappedComputers } from './mapped-computers';
import { dataItemName } from './data-item-name';
import { checkCondition } from 'src/store/selectors/conditional/conditional-check';
import { organizationDataForSuggestions } from 'src/store/selectors/conditional/organization-data-for-suggestions';
import { t } from 'src/i18n';

function calculateExternalConnectedPartners(api: ApiClient, state: State): ConnectedPartnerExternalItem[] {
  const externalConnections = externalConnectionsForMap(state);
  return externalConnections
    .filter((c) => c.type === 'Tilkoblet samarbeidspartner')
    .map((c) => ({
      externalConnection: {
        uuid: c.uuid,
        unitType: 'externalConnection',
        type: 'Tilkoblet samarbeidspartner',
        networks: networksNotDeleted(state).map((n) => ({
          value: n.uuid,
          text: n.name || t('computers.localNetworkWithoutNameTypo'),
        })),
        connectedViaNhn: c.connectedViaNhn,
        attachments: toAttachmentItems(api, 'externalConnections', c.uuid, c.attachments),
        alerts: c.alerts,
        name: c.name ?? '',
        employeeUuid: '',
        partnerUuid: c.partnerUuid ?? '',
        networkUuid: c.networkUuid ?? '',
        connectionType: c.connectionType ?? '',
        riskAssessments: c.riskAssessments,
        otherRiskAssessments: c.otherRiskAssessments,
        riskAssessmentConcludesOk: c.riskAssessmentConcludesOk,
        description: c.description ?? '',
        riskAssessmentsDisplay: riskAssessmentsDisplay(state, c.riskAssessments ?? []),
      },
    }));
}

function calculateExternalConnectedEmployees(api: ApiClient, state: State): ConnectedEmployeeExternalItem[] {
  const externalConnections = externalConnectionsForMap(state);
  return externalConnections
    .filter((c) => c.type === 'Hjemmekontor')
    .map((c) => ({
      externalConnection: {
        uuid: c.uuid,
        unitType: 'externalConnection',
        type: 'Hjemmekontor',
        networks: networksNotDeleted(state).map((n) => ({
          value: n.uuid,
          text: n.name || t('computers.localNetworkWithoutNameTypo'),
        })),
        connectedViaNhn: c.connectedViaNhn,
        attachments: toAttachmentItems(api, 'externalConnections', c.uuid, c.attachments),
        alerts: c.alerts,
        name: c.name ?? '',
        employeeUuid: c.employeeUuid ?? '',
        partnerUuid: '',
        networkUuid: c.networkUuid ?? '',
        connectionType: c.connectionType ?? '',
        riskAssessments: c.riskAssessments,
        otherRiskAssessments: c.otherRiskAssessments,
        riskAssessmentConcludesOk: c.riskAssessmentConcludesOk,
        description: c.description ?? '',
        riskAssessmentsDisplay: riskAssessmentsDisplay(state, c.riskAssessments ?? []),
      },
    }));
}

function calculateExternalCloudServices(
  api: ApiClient,
  state: State,
  infosecSettings: InfosecSettings,
): CloudServiceExternalItem[] {
  const computers = computersForMap(state);
  const availableSuppliers = partnersNotDeleted(state).map((p) => ({ value: p.uuid, text: p.name }));
  const availableContracts = contractsNotDeletedWithParties(state).map((p) => ({
    value: p.uuid,
    text: p.nameWithParties ?? p.name,
  }));
  return computers
    .filter((c) => c.unitType === 'cloudService')
    .map((c) => ({
      cloudService: {
        uuid: c.uuid,
        personalDataItems: c.personalDataItems.map((p) => mapPersonalDataItem(p, state, infosecSettings)),
        connectionType: c.connectionType ?? 'OTHER',
        unitType: 'cloudService',
        supplierName: availableSuppliers.find((e) => e.value === c.serviceProvider)?.text ?? '',
        supplierUuid: c.serviceProvider ?? '',
        contractName: availableContracts.find((e) => e.value === c.serviceProviderContract)?.text ?? '',
        contractUuid: c.serviceProviderContract ?? '',
        serviceName: c.name ?? '',
        availableSuppliers: availableSuppliers,
        availableContracts: availableContracts,
        attachments: toAttachmentItems(api, 'computers', c.uuid, c.attachments),
        alerts: c.alerts ?? [],
        deletable: storageUnitIsDeletable(c.uuid, infosecSettings.dataItems),
      },
    }));
}

function storageUnitIsDeletable(uuid: string, dataItems: InfosecDataItem[]) {
  const f = dataItems.find((i) => i.storageUnit === uuid);
  return f === undefined;
}

function calculateExternalRemoteServers(
  api: ApiClient,
  state: State,
  infosecSettings: InfosecSettings,
): RemoteServerExternalItem[] {
  const computers = computersForMap(state);
  const availableSuppliers = partnersNotDeleted(state).map((p) => ({ value: p.uuid, text: p.name }));
  const availableContracts = contractsNotDeletedWithParties(state).map((p) => ({
    value: p.uuid,
    text: p.nameWithParties || p.name,
  }));
  return computers
    .filter((c) => c.unitType === 'remoteServer')
    .map((c) => {
      const supplierName = availableSuppliers.find((e) => e.value === c.serviceProvider)?.text ?? '';
      return {
        remoteServer: {
          uuid: c.uuid,
          personalDataItems: c.personalDataItems.map((p) => mapPersonalDataItem(p, state, infosecSettings)),
          connectionType: c.connectionType || 'OTHER',
          unitType: 'remoteServer',
          supplierName: supplierName,
          supplierUuid: c.serviceProvider ?? '',
          contractName: availableContracts.find((e) => e.value === c.serviceProviderContract)?.text ?? '',
          contractUuid: c.serviceProviderContract ?? '',
          serviceName: c.name ?? '',
          availableSuppliers: availableSuppliers,
          availableContracts: availableContracts,
          attachments: toAttachmentItems(api, 'computers', c.uuid, c.attachments),
          alerts: c.alerts ?? [],
          simplifiedPrivacy: variantSimplifiedPrivacy(state),
          displayName: supplierName || t('computers.remoteServer'),
          deletable: storageUnitIsDeletable(c.uuid, infosecSettings.dataItems),
        },
      };
    });
}

function calculateNetworks(state: State, infosecSettings: InfosecSettings): Network[] {
  const availableSuppliers = partnersNotDeleted(state).map((p) => ({ value: p.uuid, text: p.name }));
  return networksForMap(state).map((n) => {
    return {
      uuid: n.uuid,
      name: n.name || t('computers.networkWithoutName'),
      connection: {
        uuid: n.uuid,
        unitType: 'connection',
        partners: availableSuppliers,
        connectionType: n.connectionType ?? 'NONE',
        alerts: [],
        ghost: n.connectionType === 'NONE',
        supplierUuid: n.supplierUuid ?? '',
      },
      firewall: {
        uuid: n.uuid,
        unitType: 'firewall',
        ghost: n.firewall === 'NONE',
        alerts: [],
        firewallType: n.firewall ?? '',
        firewallName: n.firewallName ?? '',
      },
      item: {
        uuid: n.uuid,
        partners: availableSuppliers,
        name: n.name ?? '',
        type: n.networkType || NetworkViewModelNetworkTypeEnum.Cable,
        unitType: 'network',
        attachments: [],
        alerts: n.alerts,
        supplierUuid: n.supplierUuid ?? '',
        antiVirusOperator: n.antiVirusOperator ?? '',
        antiVirusOperatorType: n.antiVirusOperatorType ?? '',
        systemUpdateOperator: n.systemUpdateOperator ?? '',
        systemUpdateOperatorType: n.systemUpdateOperatorType ?? '',
        connectionType: n.connectionType ?? 'NONE',
        firewallType: n.firewall ?? '',
        firewallName: n.firewallName ?? '',
      },
      computers: n.computers.map((c) => ({
        uuid: c.uuid,
        networks: networksNotDeleted(state).map((n) => ({
          uuid: n.uuid,
          name: n.name || t('computers.localNetworkWithoutName'),
        })),
        name: c.name ?? '',
        mobileMedicalDataRequirementsOptions: mobileMedicalDataRequirementsOptions(state).map((o) => ({
          value: o.id,
          text: o.value,
        })),
        partners: availableSuppliers,
        antiVirusRequirementsOptions: antiVirusRequirementsOptions(state).map((o) => ({ value: o.id, text: o.value })),
        systemUpdateOperatorType: c.systemUpdateOperatorType ?? '',
        systemUpdateOperator: c.systemUpdateOperator ?? '',
        antiVirusOperator: c.antiVirusOperator ?? '',
        antiVirusOperatorType: c.antiVirusOperatorType ?? '',
        locked: c.locked ?? false,
        antiVirusRequirements: c.antiVirusRequirements ?? [],
        location: c.location ?? '',
        elevated: c.elevated ?? false,
        restrictedPhysicalAccess: c.restrictedPhysicalAccess ?? false,
        networkUuid: c.networkUuid ?? '',
        mobileMedicalDataRequirements: c.mobileMedicalDataRequirements ?? [],
        notes: c.notes ?? '',
        displayPositionSecure: c.displayPositionSecure ?? false,
        unitType: 'computer',
        type: c.type ?? '',
        displayName: c.name || _displayTypeName(c.unitType, c.type),
        attachments: (c.attachments ?? []).map((x) => displayName(x.name, t('computers.withoutName'))),
        hasMedicalData: hasMedicalData(c.uuid, c.personalDataItems),
        computerIsInNetworkWithMedicalData: networkHasMedicalData(n),
        deletable: storageUnitIsDeletable(c.uuid, infosecSettings.dataItems),
        personalDataItems: c.personalDataItems.map((p) => mapPersonalDataItem(p, state, infosecSettings)),
        alerts: c.alerts,
      })),
    };
  });
}

function hasMedicalData(uuid: string, personalDataItems: PersonalDataItemViewModel[]): boolean {
  for (const item of personalDataItems) {
    if (item.storageUnit === uuid && item.type?.startsWith('patientData')) {
      return true;
    }
  }
  return false;
}

function networkHasMedicalData(
  network: NetworkViewModel & { computers: ComputerViewModelExtended[]; alerts: string[] },
): boolean {
  return network.computers.find((c) => hasMedicalData(c.uuid, c.personalDataItems)) !== undefined;
}

function _displayTypeName(unitType, type) {
  if (unitType === 'remoteServer') {
    return t('computers.remoteServer');
  }
  if (unitType === 'cloudService') {
    return t('computers.options.cloudService');
  }
  if (unitType === 'remoteServer') {
    return t('computers.remoteServer');
  }
  if (unitType === 'personalDataChannel') {
    return t('computers.patientCommunication');
  }
  if (unitType === 'printer') {
    return t('computers.printer.typeName');
  }
  if (type === 'Server') {
    return t('computers.options.server');
  }
  if (type === 'Arbeidsstasjon') {
    return t('computers.options.workstationClient');
  }
  if (type === 'mobile') {
    return t('computers.options.mobileDevice');
  }
  return t('computers.localDevice');
}

function storageUnits(state: State): SelectDropdownOption[] {
  const computers = computersNotDeleted(state);
  const p = partnerNamesById(state);
  const list: SelectDropdownOption[] = [];
  if (computers.length > 0) {
    let is = false;

    for (const item1 of computers) {
      if (item1.unitType === 'computer') {
        is = true;
        let name: string;
        if (item1.name && item1.name.length > 0) {
          name = item1.name;
        } else {
          let type = item1.type;
          if (item1.type === 'UNDEFINED') {
            type = t('computers.unitWord');
          }
          name = t('computers.unitWithoutNameSuffix', { type });
        }
        list.push({
          value: item1.uuid,
          text: t('computers.localUnitNamed', { name }),
        });
      }
    }
    if (is) {
      list.push({ value: 'computer', text: t('computers.otherLocalUnit') });
    } else {
      list.push({ value: 'computer', text: t('computers.localDevice') });
    }

    is = false;
    for (const item1 of computers) {
      if (item1.unitType === 'remoteServer') {
        is = true;
        const name = item1.serviceProvider ? p[item1.serviceProvider] : '';
        list.push({
          value: item1.uuid,
          text: t('computers.remoteServerNamed', { name }),
        });
      }
    }
    if (is) {
      list.push({
        value: 'remoteServer',
        text: t('computers.otherRemoteServer'),
      });
    } else {
      list.push({ value: 'remoteServer', text: t('computers.remoteServer') });
    }
    is = false;
    for (const item1 of computers) {
      if (item1.unitType === 'cloudService') {
        is = true;
        const name = displayName(item1.serviceProvider ? p[item1.serviceProvider] : '', t('computers.withoutName'));
        list.push({
          value: item1.uuid,
          text: t('computers.cloudServiceNamed', { name }),
        });
      }
    }
    if (is) {
      list.push({
        value: 'cloudService',
        text: t('computers.otherCloudService'),
      });
    } else {
      list.push({ value: 'cloudService', text: t('computers.options.cloudService') });
    }
    return list;
  }
  return [
    { value: 'computer', text: t('computers.localDevice') },
    { value: 'remoteServer', text: t('computers.remoteServer') },
    { value: 'cloudService', text: t('computers.options.cloudService') },
  ];
}

function mapPersonalDataItem(
  p: PersonalDataItemViewModelExtended,
  state: State,
  infosecSettings: InfosecSettings,
): DataItem {
  const item = infosecSettings.dataItems.find((i) => i.uuid === p.uuid);

  if (item === undefined) {
    throw new Error('Illegal state');
  }

  const riskAssessments = riskAssessmentsNotDeleted(state);

  const suggestedRiskAssessments: SuggestedRiskAssessment[] = suggestedRiskAssessmentsForDataItem(
    organizationDataForSuggestions(state),
    suggestedRiskAssessmentsForDataItems(),
    p.uuid,
  );

  return {
    ...item,
    availableContracts: contractsNotDeletedWithParties(state).map((p) => ({
      value: p.uuid,
      text: p.nameWithParties || p.name,
    })),
    availableSuppliers: infosecSettings.partners,
    availableCloudServices: infosecSettings.cloudServices,
    availableComputers: infosecSettings.computers,
    availableAssets: infosecSettings.assets.map((a) => ({ value: a.uuid, text: a.name + ' ' + a.number })),
    availableEmployees: infosecSettings.employees,
    availableLocations: infosecSettings.locations,
    sharedMedicalRecordsPartners: p.sharedMedicalRecordsPartners ?? [],
    sharedMedicalRecords: p.sharedMedicalRecords ?? false,
    autoUpdateAddressNhn: p.autoUpdateAddressNhn ?? false,
    autoLookupAddressNhn: p.autoLookupAddressNhn ?? false,
    autoLookupAddress: p.autoLookupAddress ?? false,
    autoLookupPatients: p.autoLookupPatients ?? false,
    messagingSupplier: p.messagingSupplier ?? '',
    messageMonitoring: p.messageMonitoring ?? false,
    messagingTypeOther: p.messagingTypeOther ?? '',
    messagingType: p.messagingType ?? '',
    availableRemoteServers: infosecSettings.remoteServers,
    unitType: 'personalDataItem',
    alerts: p.alerts,
    riskAssessmentConcludesOk: p.riskAssessmentConcludesOk ?? false,
    consentContracts: p.consentContracts ?? [],
    legalBasis: p.legalBasis ?? '',
    purpose: p.purpose ?? '',
    sharedMedicalRecordsContracts:
      p.sharedMedicalRecordsContracts === undefined ? '' : (p.sharedMedicalRecordsContracts[0] ?? ''),
    journalHasElectronicMessaging: p.journalHasElectronicMessaging ?? false,
    patientDialogueRequirements: p.patientDialogueRequirements ?? [],
    systemCapabilities: p.systemCapabilities ?? [],
    storageUnit: p.storageUnit ?? '',
    relatedAssets: p.relatedAssets ?? [],
    notes: p.notes ?? '',
    name: p.name ?? '',
    personalData: p.personalData ?? false,
    relatedRiskAssessments: _relatedRiskAssessments(p, suggestedRiskAssessments, riskAssessments),
  };
}

function suggestedRiskAssessmentsForDataItem(
  organization: any,
  suggestedRiskAssessments: SuggestedRiskAssessment[],
  dataItemUuid: string,
): SuggestedRiskAssessment[] {
  return suggestedRiskAssessments.filter((s) => {
    return checkCondition(organization, s.condition.expression, s.condition.context, undefined, dataItemUuid);
  });
}
function _relatedRiskAssessments(
  dataItem: PersonalDataItemViewModelExtended,
  suggestedRiskAssessments: SuggestedRiskAssessment[],
  riskAssessments: RiskAssessmentViewModel[],
): RelatedRiskAssessment[] {
  const result: RelatedRiskAssessment[] = [];
  suggestedRiskAssessments.forEach((item) => {
    const createdFromTemplate = riskAssessments.filter(
      (r) => r.relatedDataItem === dataItem.uuid && r.createdFromTemplate === item.pageId,
    );
    if (createdFromTemplate.length) {
      const c = createdFromTemplate[0];
      let risk: number | null = null;
      if (c.probability && c.consequence) {
        risk = c.probability * c.consequence;
      }
      result.push({
        suggestion: false,
        uuid: c.uuid,
        name: entityNameForLists(c.name, c.draftName, c.isConfirmedEntity),
        risk: risk ?? 0,
        hasDraft: c.hasDraft,
      });
    } else {
      result.push({
        suggestion: true,
        templateId: item.pageId,
        name: item.name,
        risk: 0,
        uuid: '',
        hasDraft: false,
      });
    }
  });
  riskAssessments = sortBy(riskAssessments, (item) => item.name);
  riskAssessments.forEach(function (r) {
    const s = suggestedRiskAssessments.filter(function (item) {
      return item.pageId === r.createdFromTemplate;
    });
    if (r.relatedDataItem === dataItem.uuid && (!r.createdFromTemplate || s.length === 0)) {
      let risk: any = null;
      if (r.probability && r.consequence) {
        risk = r.probability * r.consequence;
      }
      result.push({
        suggestion: false,
        uuid: r.uuid,
        name: entityNameForLists(r.name, r.draftName, r.isConfirmedEntity),
        risk: risk,
        hasDraft: r.hasDraft,
      });
    }
  });
  return result;
}

function calculateOfflinePersonalDataItems(state: State, infosecSettings: InfosecSettings): DataItem[] {
  return offlinePersonalDataItems(state).map((p) => mapPersonalDataItem(p, state, infosecSettings));
}

function calculateStandaloneComputers(state: State, infosecSettings: InfosecSettings): ComputerMapLocal[] {
  const availableSuppliers = partnersNotDeleted(state).map((p) => ({ value: p.uuid, text: p.name }));

  return standaloneComputers(state).map((c) => ({
    uuid: c.uuid,
    networks: networksNotDeleted(state).map((n) => ({
      uuid: n.uuid,
      name: n.name || t('computers.localNetworkWithoutName'),
    })),
    name: c.name ?? '',
    mobileMedicalDataRequirementsOptions: mobileMedicalDataRequirementsOptions(state).map((o) => ({
      value: o.id,
      text: o.value,
    })),
    partners: availableSuppliers,
    antiVirusRequirementsOptions: antiVirusRequirementsOptions(state).map((o) => ({ value: o.id, text: o.value })),
    systemUpdateOperatorType: c.systemUpdateOperatorType ?? '',
    systemUpdateOperator: c.systemUpdateOperator ?? '',
    antiVirusOperator: c.antiVirusOperator ?? '',
    antiVirusOperatorType: c.antiVirusOperatorType ?? '',
    locked: c.locked ?? false,
    antiVirusRequirements: c.antiVirusRequirements ?? [],
    location: c.location ?? '',
    elevated: c.elevated ?? false,
    restrictedPhysicalAccess: c.restrictedPhysicalAccess ?? false,
    networkUuid: c.networkUuid ?? '',
    mobileMedicalDataRequirements: c.mobileMedicalDataRequirements ?? [],
    notes: c.notes ?? '',
    displayPositionSecure: c.displayPositionSecure ?? false,
    unitType: 'computer',
    type: c.type ?? '',
    displayName: '',
    attachments: [],
    hasMedicalData: hasMedicalData(c.uuid, c.personalDataItems),
    computerIsInNetworkWithMedicalData: false,
    deletable: storageUnitIsDeletable(c.uuid, infosecSettings.dataItems),
    personalDataItems: c.personalDataItems.map((p) => mapPersonalDataItem(p, state, infosecSettings)),
    alerts: c.alerts,
  }));
}
function calculateStandalonePrinters(state: State): ComputerMapPrinter[] {
  return standalonePrinters(state).map((p) => ({
    uuid: p.uuid,
    networkUuid: p.networkUuid ?? '',
    name: p.name ?? '',
    notes: p.notes ?? '',
    printerPositionSecure: p.printerPositionSecure,
    location: p.location ?? '',
    unitType: 'printer',
    attachments: [],
    alerts: p.alerts,
  }));
}

function availableRiskAssessments(
  state: State,
): { uuid: string; name: string; pageId?: number; consequence?: number; probability?: number }[] {
  return riskAssessmentsNotDeleted(state).map((r) => ({
    uuid: r.uuid,
    name: r.name ?? '',
    pageId: r.pageId,
    consequence: r.consequence,
    probability: r.probability,
  }));
}

export const mappedNetworks = (networks): { uuid: string; name: string; type: string; connectionType: string }[] =>
  networks
    .filter((item) => {
      return !item.deleted;
    })
    .map((item) => {
      return {
        uuid: item.uuid,
        name: item.name,
        type: item.type,
        connectionType: item.connectionType,
      };
    });

export const mappedPartners = (partners): { value: string; text: string }[] =>
  partners
    .filter((item) => {
      return !item.deleted && item.isConfirmedEntity;
    })
    .map((item) => {
      return {
        value: item.uuid,
        text: item.name,
      };
    });

export const mappedCloudServices = (computers): InfosecStorageUnit[] =>
  computers
    .filter((item) => {
      return !item.deleted && item.unitType === 'cloudService';
    })
    .map((item) => {
      return {
        uuid: item.uuid,
        name: item.name ?? '',
        unitType: 'cloudServices',
        computerType: '',
        application: '',
        partnerUuid: item.serviceProvider ?? '',
        networkUuid: item.networkUuid ?? '',
      };
    });

function calculateInfosecSettings(state: State) {
  const locations = _.uniqBy(
    personalDataItemsNotDeleted(state)
      .filter((p) => p.type?.endsWith('.paperDocuments'))
      .map((p): InfosecStorageUnit => {
        return {
          uuid: p.storageLocation ?? '',
          name: p.storageLocation ?? '',
          unitType: 'locations',
          computerType: '',
          application: '',
          partnerUuid: '',
          networkUuid: '',
        };
      }),
    (e) => e.uuid,
  );
  const assets = assetsNotDeleted(state)
    .filter((a) => a.isConfirmedEntity)
    .map((a) => {
      return {
        uuid: a.uuid,
        name: a.name ?? '',
        number: a.number ?? '',
      };
    });
  const computers = mappedComputers(computersNotDeleted(state));
  return {
    computers: computers,

    dataItems: _.orderBy(
      mappedDataItems(personalDataItemsNotDeleted(state), computersNotDeleted(state), computers, assets, locations),
      [
        (o) => o.createdDateTime,
        (o) => {
          const dataItemName1 = dataItemName(o, computers, assets, locations);
          if (dataItemName1 === null) {
            console.log(o);
            return '?';
          }
          return dataItemName1?.toLowerCase() ?? '';
        },
        (o) => o.uuid,
      ],
    ),
    employees: employeesNotDeleted(state)
      .filter((e) => e.status !== EmployeeViewModelStatusEnum.Terminated && e.isConfirmedEntity)
      .map((p) => ({ value: p.uuid, text: p.name })),
    partners: mappedPartners(partnersNotDeleted(state)),
    remoteServers: mappedRemoteServers(computersNotDeleted(state)),
    cloudServices: mappedCloudServices(computersNotDeleted(state)),
    locations: locations,
    assets: assets,
    networks: mappedNetworks(networksNotDeleted(state)),
    externalConnections: externalConnectionsForMap(state).map(
      (e: ExternalConnectionViewModel): InfosecExternalConnection => {
        if (e.type === 'Hjemmekontor') {
          return {
            type: 'Hjemmekontor',
            uuid: e.uuid,
            connectionType: e.connectionType ?? 'NONE',
            networkUuid: e.networkUuid ?? '',
            employeeUuid: e.employeeUuid ?? '',
          };
        } else {
          return {
            type: 'Tilkoblet samarbeidspartner',
            uuid: e.uuid,
            connectionType: e.connectionType ?? 'NONE',
            networkUuid: e.networkUuid ?? '',
            partnerUuid: e.partnerUuid ?? '',
          };
        }
      },
    ),
  };
}

export function computersPageView(
  api: ApiClient,
  hrefPrefix: string,
  viewModel: AbstractPageView,
  state: State,
): ComputersPageViewModel {
  const infosecSettings = calculateInfosecSettings(state);
  const linkedPage = getPage(65, getPages(state));
  const pageLinks: PageLinks = {
    type: 'page-links',
    items: [
      {
        name: t('computers.goToThemePage', { name: linkedPage?.name }) || '',
        type: '',
        href: '/account/' + getOrganizationId(state) + '/' + 65,
      },
    ],
  };
  return {
    ...viewModel,
    organizationName: displayName(getOrganization(state)?.name),
    antiVirusRequirementsOptions: antiVirusRequirementsOptions(state).map((o) => ({ value: o.id, text: o.value })),
    type: 'computers-page',
    icon: 'computers',
    href: hrefPrefix,
    connectedPartners: calculateExternalConnectedPartners(api, state),
    connectedEmployees: calculateExternalConnectedEmployees(api, state),
    cloudServices: calculateExternalCloudServices(api, state, infosecSettings),
    remoteServers: calculateExternalRemoteServers(api, state, infosecSettings),
    networks: calculateNetworks(state, infosecSettings),
    offlinePersonalDataItems: calculateOfflinePersonalDataItems(state, infosecSettings),
    standaloneComputers: calculateStandaloneComputers(state, infosecSettings),
    standalonePrinters: calculateStandalonePrinters(state),
    availableEmployees: employeesNotDeleted(state)
      .filter((e) => e.isConfirmedEntity)
      .map((e) => ({ value: e.uuid, text: e.name })),
    availablePartners: partnersNotDeleted(state).map((p) => ({ value: p.uuid, text: p.name })),
    availableContracts: contractsNotDeletedWithParties(state).map((p) => ({
      value: p.uuid,
      text: p.nameWithParties || p.name,
    })),
    availableRiskAssessments: availableRiskAssessments(state).map((r) => ({
      name: r.name,
      uuid: r.uuid,
      riskDisplay: riskDisplay(r.probability, r.consequence),
      pageId: r.pageId,
    })),
    availableStorageUnits: storageUnits(state),
    patientDialogueRequirementsOptions: [],
    infosecSettings: infosecSettings,
    pageLinks,
  };
}

export function riskDisplay(probability: number | undefined, consequence: number | undefined) {
  if (probability === undefined || consequence === undefined) {
    return t('computers.riskNotAssessed');
  }
  const risk = probability * consequence;
  if (risk === 0) {
    return t('computers.riskNotAssessed');
  }
  if (risk > 0 && risk < 5) {
    return t('computers.riskLow');
  }
  if (risk > 4 && risk < 9) {
    return t('computers.riskModerate');
  }
  if (risk > 8 && risk < 13) {
    return t('computers.riskHigh');
  }
  if (risk > 12) {
    return t('computers.riskVeryHigh');
  }
  throw new Error('Illegal state');
}

function riskAssessmentsDisplay(
  state: State,
  riskAssessments: string[],
): { name: string; riskDisplay: string; href: string }[] {
  const o = getOrganizationId(state);
  const a = availableRiskAssessments(state);
  return a
    .filter((r) => riskAssessments.includes(r.uuid))
    .map((r) => ({
      name: r.name,
      href: '/account/' + o + '/20/riskAssessments/' + r.uuid,
      riskDisplay: riskDisplay(r.probability, r.consequence),
    }));
}
